import { useEffect, useRef, useState, type ChangeEvent } from 'react'
import { omitTransientProps } from '../../lib/omitTransientProps'
import { styled } from '@linaria/react'
import { minimalVars } from '@ovasabi/ui-minimal/tokens'
import { motion } from 'framer-motion'
import { MinimalButton } from '@ovasabi/ui-minimal'
import { mediaUrl, uploadImage, type UploadScope } from '../../lib/runtime'
import { IMAGE_ACCEPT, type ImageAdvisory } from '../../lib/imageProcessing'
import { ImageThumb } from './chefShared'

/**
 * useImagePicker centralizes the file-input + upload-seam plumbing the chef
 * surfaces share. The hook returns refs/state; the matching <CoverPicker /> +
 * <InlinePhotoPicker /> components render one shape consistently across menu
 * cover, dish photo, and (later) chef avatar. Images are optional everywhere.
 *
 * Editing an entity that already has a photo seeds the picker with it
 * (`initialUrl`), so the frame shows the current picture and offers to replace
 * it. Without that seed an edit screen opened on an empty "Add cover image"
 * box, which reads as "this menu has no photo" for a menu that plainly does.
 */
export interface ImagePickerState {
  scope: UploadScope
  inputRef: React.RefObject<HTMLInputElement | null>
  preview: string | undefined
  uploading: boolean
  error: string | undefined
  url: string | undefined
  /** True once the user has chosen a new file this session. */
  changed: boolean
  /** True when there is a picture to show — either the existing one or a new one. */
  hasImage: boolean
  /** Soft advice about the photo just picked. Shown, never blocking. */
  advisories: ImageAdvisory[]
  pick: () => void
  reset: () => void
  onFile: (event: ChangeEvent<HTMLInputElement>) => Promise<void>
}

export const useImagePicker = (
  scope: UploadScope,
  ownerId: string,
  /** The entity's current photo, when editing. Shown until a new file is picked. */
  initialUrl?: string,
): ImagePickerState => {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const selection = useRef(0)
  const objectUrl = useRef<string | undefined>(undefined)
  const releasePreview = () => {
    if (objectUrl.current) URL.revokeObjectURL(objectUrl.current)
    objectUrl.current = undefined
  }
  useEffect(() => () => { selection.current++; releasePreview() }, [])
  const [preview, setPreview] = useState<string | undefined>(initialUrl || undefined)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | undefined>()
  const [url, setUrl] = useState<string | undefined>()
  const [changed, setChanged] = useState(false)
  const [advisories, setAdvisories] = useState<ImageAdvisory[]>([])

  // The record can arrive after first paint (projection hydration), so adopt a
  // late-arriving photo — but never over a file the user has already chosen.
  useEffect(() => {
    if (changed) return
    setPreview(initialUrl || undefined)
  }, [initialUrl, changed])

  const pick = () => inputRef.current?.click()
  const reset = () => {
    selection.current++
    releasePreview()
    setUploading(false)
    setPreview(initialUrl || undefined)
    setUrl(undefined)
    setError(undefined)
    setChanged(false)
    setAdvisories([])
    if (inputRef.current) inputRef.current.value = ''
  }
  const onFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    const version = ++selection.current
    releasePreview()
    setError(undefined)
    setUrl(undefined)
    setAdvisories([])
    setChanged(true)
    objectUrl.current = URL.createObjectURL(file)
    setPreview(objectUrl.current)
    setUploading(true)
    try {
      const uploaded = await uploadImage(file, scope, ownerId)
      if (version !== selection.current) return
      setUrl(uploaded.url)
      setAdvisories(uploaded.advisories ?? [])
      releasePreview()
      setPreview(uploaded.url)
    } catch (err) {
      if (version !== selection.current) return
      setError(err instanceof Error ? err.message : 'upload failed')
      setUrl(undefined)
    } finally {
      if (version === selection.current) setUploading(false)
    }
  }
  return {
    scope,
    inputRef,
    preview,
    uploading,
    error,
    url,
    changed,
    hasImage: Boolean(preview),
    advisories,
    pick,
    reset,
    onFile,
  }
}

/* ── soft photo advice ───────────────────────────────────────────────── */

const AdviceList = styled.ul`
  margin: ${minimalVars.space['2xs']} 0 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: ${minimalVars.space['2xs']};
  font-size: ${minimalVars.typography.captionSize};
  color: ${minimalVars.color.textSecondary};
  text-align: left;

  & > li {
    padding-left: ${minimalVars.space.xs};
    border-left: 2px solid ${minimalVars.color.warning};
  }
`

/**
 * Tips about a photo that has already uploaded: too dark, too small, a
 * screenshot that cards will crop. Advice only — the photo is saved either
 * way, and the chef decides whether to replace it.
 */
export const PhotoAdvice = ({ advisories }: { advisories?: readonly ImageAdvisory[] }) =>
  advisories?.length ? (
    <AdviceList aria-label="Photo tips">
      {advisories.map((advice) => (
        <li key={advice.code}>{advice.message}</li>
      ))}
    </AdviceList>
  ) : null

/* ── centered cover picker (menu builder, chef avatar) ──────────────── */

/** "4 / 3" in everyday words: what shape the photo will be shown at. */
const frameShape = (ratio: string): string => {
  const [w, h] = ratio.split('/').map((part) => Number(part.trim()))
  if (!w || !h || Math.abs(w - h) < 0.01) return 'a square'
  return w > h ? 'a wide rectangle' : 'a tall rectangle'
}

// motion would pass `$` props to the DOM; styled-components used to strip them.
const CoverFrame = styled(omitTransientProps(motion.button))<{ $has: boolean; $size: number; $ratio: string }>`
  position: relative;
  display: block;
  /* Left-aligned per design feedback — sits flush with the rest of the form
     fields below it, no centering. */
  margin: 0;
  width: ${({ $size }) => `${$size}px`};
  max-width: 100%;
  /* Frame the crop the way the card will: a menu cover is shown 4:3 on the
     menu card, so cropping it square here would have the chef composing a
     photo that gets cut differently everywhere it appears. */
  aspect-ratio: ${({ $ratio }) => $ratio};
  border-radius: ${minimalVars.radius.md};
  border: 1.5px dashed
    ${({ $has }) => ($has ? 'transparent' : minimalVars.color.borderSubtle)};
  background-color: ${minimalVars.color.bgSurface};
  background-size: cover;
  background-position: center;
  cursor: pointer;
  padding: 0;
  overflow: hidden;
  transition: border-color 180ms ease;
  &:hover {
    border-color: ${minimalVars.color.textTertiary};
  }
`

const CoverOverlay = styled.div<{ $always?: boolean }>`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: ${minimalVars.color.textPrimary};
  background: ${({ $always }) => ($always ? 'transparent' : minimalVars.color.bgOverlay)};
  opacity: ${({ $always }) => ($always ? 1 : 0)};
  transition: opacity 180ms ease;
  ${CoverFrame}:hover & {
    opacity: 1;
  }
`

const CoverHint = styled.span`
  font-size: ${minimalVars.typography.captionSize};
  color: ${minimalVars.color.textTertiary};
  text-align: left;
  display: block;
  margin-top: ${minimalVars.space['2xs']};
`

const CoverGlyph = styled.span<{ $compact?: boolean }>`
  /* A decorative mark centred in a fixed square frame. The pair steps 22px to
     32px, and no type token carries that range. */
  font-size: ${({ $compact }) => ($compact ? '22px' : '32px')};
  line-height: 1;
`

const CoverLabel = styled.span<{ $compact?: boolean }>`
  font-weight: ${minimalVars.typography.weightSemibold};
  font-size: ${({ $compact }) =>
    $compact ? minimalVars.typography.captionSize : minimalVars.typography.bodySize};
  text-align: center;
  padding: 0 6px;
`

/**
 * CoverPicker — a centered square frame that *is* the upload button. Empty
 * state shows a glyph + label; the image preview lives inside the frame, and
 * the overlay appears on hover so the user can swap without losing the
 * picture. This replaces the awkward thumbnail-beside-button layout.
 */
export const CoverPicker = ({
  picker,
  label = 'Add cover image',
  helper,
  glyph = '📷',
  size = 320,
  ratio = '4 / 3',
  showHelper = true,
}: {
  picker: ImagePickerState
  label?: string
  helper?: string
  glyph?: string
  /** Frame width in px (capped to the container). Height follows `ratio`. */
  size?: number
  /**
   * Crop the frame presents, matching where the photo will be shown:
   * `4 / 3` for menu covers and dish cards, `1 / 1` for avatars and thumbs.
   */
  ratio?: string
  /** Hide the caption hint when space is tight (e.g. inline next to other fields). */
  showHelper?: boolean
}) => {
  const has = picker.hasImage
  const compact = size <= 130
  const defaultHelper = ratio
    ? `Optional. It shows as ${frameShape(ratio)}, so keep the food in the middle.`
    : undefined
  return (
    <div>
      <CoverFrame
        type="button"
        $has={has}
        $size={size}
        $ratio={ratio}
        onClick={picker.pick}
        whileTap={{ scale: 0.985 }}
        aria-label={has ? 'Replace photo' : label}
        style={picker.preview ? { backgroundImage: `url("${mediaUrl(picker.preview, 768)}")` } : undefined}
      >
        <CoverOverlay $always={!has}>
          {!has && <CoverGlyph $compact={compact} aria-hidden>{glyph}</CoverGlyph>}
          <CoverLabel $compact={compact}>{has ? 'Replace photo' : label}</CoverLabel>
        </CoverOverlay>
      </CoverFrame>
      {(picker.uploading || picker.error || (showHelper && (helper ?? defaultHelper))) && (
        <CoverHint>
          {picker.uploading
            ? 'Uploading…'
            : picker.error
              ? `Upload failed: ${picker.error}`
              : (helper ?? defaultHelper)}
        </CoverHint>
      )}
      {!picker.uploading && <PhotoAdvice advisories={picker.advisories} />}
      {picker.changed && (
        <MinimalButton type="button" variant="ghost" onClick={picker.reset}>
          Cancel photo change
        </MinimalButton>
      )}
      <input
        ref={picker.inputRef}
        type="file"
        accept={IMAGE_ACCEPT}
        onChange={picker.onFile}
        style={{ display: 'none' }}
      />
    </div>
  )
}

/* ── inline picker (dish rows, where space is tight) ────────────────── */

const InlineRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${minimalVars.space.xs};

  /* Advice reads as a sentence; give it its own line under the controls. */
  & > ul {
    flex-basis: 100%;
  }
`

/** Compact picker for dense forms — the thumbnail sits next to a button. */
export const InlinePhotoPicker = ({
  picker,
  label = 'Add photo',
}: {
  picker: ImagePickerState
  label?: string
}) => (
  <InlineRow>
    <ImageThumb $src={picker.preview} />
    <MinimalButton type="button" variant="secondary" onClick={picker.pick}>
      {picker.preview ? 'Replace photo' : label}
    </MinimalButton>
    {picker.uploading && <span style={{ fontSize: 12, opacity: 0.7 }}>Uploading…</span>}
    {picker.error && <span role="alert">{picker.error}</span>}
    {!picker.uploading && <PhotoAdvice advisories={picker.advisories} />}
    {picker.changed && (
      <MinimalButton type="button" variant="ghost" onClick={picker.reset}>
        Cancel photo change
      </MinimalButton>
    )}
    <input
      ref={picker.inputRef}
      type="file"
      accept={IMAGE_ACCEPT}
      onChange={picker.onFile}
      style={{ display: 'none' }}
    />
  </InlineRow>
)
